<template>
  <div class="container mt-5">
    <div class="hero-section mb-4">
      <div class="hero-overlay">
        <h1 class="hero-title">Board & Card Game Prototyper</h1>
        <p class="hero-subtitle">Forge your next great game</p>
        <router-link v-if="user" to="/create-board-game" class="btn btn-primary btn-lg hero-cta">
          Create
        </router-link>
        <router-link v-else to="/register" class="btn btn-primary btn-lg hero-cta">
          Join
        </router-link>
      </div>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="clearSuccessMessage"></button>
    </div>

    <!-- Authenticated Quick Actions -->
    <div v-if="user" class="row">
       <div class="col-md-2 mb-2">
        <router-link to="/board-games" class="btn btn-outline-primary btn-sm btn-block">
          All Board Games
        </router-link>
      </div>
       <div class="col-md-2 mb-2">
        <router-link to="/create-board-game" class="btn btn-success btn-sm btn-block">
          Create Game
        </router-link>
      </div>
       <div class="col-md-2 mb-2">
        <router-link to="/design" class="btn btn-primary btn-sm btn-block">
          Game Designer
        </router-link>
      </div>
    </div>

    <!-- Guest Value Proposition -->
    <div v-if="!user" class="guest-value-section mt-4">
      <div class="row row-cols-1 row-cols-md-3 g-4 mb-5">
        <div class="col" v-for="f in features" :key="f.title">
          <div class="card h-100 feature-card">
            <div class="feature-art">
              <img :src="f.image" :alt="f.alt" loading="lazy" />
            </div>
            <div class="card-body text-center">
              <h5 class="card-title" style="font-family: var(--font-heading);">{{ f.title }}</h5>
              <p class="card-text text-muted mb-0">{{ f.body }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Sample gallery. These are rendered by the same card templates the
           app and the print PDF use, so what a visitor sees here is what they
           get — not a mock-up. -->
      <div class="sample-showcase mb-5">
        <h3 class="text-center mb-2" style="font-family: var(--font-heading);">What You Can Create</h3>
        <p class="text-center text-muted mb-4">Straight out of the generator — no touch-ups.</p>
        <div class="sample-grid">
          <figure class="sample-item" v-for="s in samplePortrait" :key="s.src">
            <img :src="s.src" :alt="s.label" loading="lazy" />
            <figcaption>{{ s.label }}</figcaption>
          </figure>
        </div>
        <div class="sample-grid mt-3">
          <figure class="sample-item" v-for="s in sampleLandscape" :key="s.src">
            <img :src="s.src" :alt="s.label" loading="lazy" />
            <figcaption>{{ s.label }}</figcaption>
          </figure>
        </div>
      </div>

    </div>
    
    <!-- Pending Editor Invites Section -->
    <div v-if="user && pendingInvites.length > 0" class="mt-4">
      <div class="card">
        <div class="card-header bg-warning text-dark">
          <h5 class="mb-0">Pending Editor Invites</h5>
        </div>
        <div class="card-body">
          <p>You have been invited to edit the following board games:</p>
          <div class="list-group">
            <div 
              v-for="invite in pendingInvites" 
              :key="invite.id" 
              class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            >
              <div class="d-flex align-items-center">
                <div class="game-icon bg-primary text-white me-3 d-flex align-items-center justify-content-center">
                  <i class="fas fa-dice"></i>
                </div>
                <div>
                  <h6 class="mb-1">{{ invite.boardGame.name }}</h6>
                  <div class="d-flex align-items-center">
                    <div class="user-icon bg-warning text-dark me-2 d-flex align-items-center justify-content-center">
                      <i class="fas fa-user"></i>
                    </div>
                    <div>
                      <small class="text-muted">
                        Invited by <strong>{{ invite.boardGame.ownerName || 'Game Owner' }}</strong> 
                        on {{ formatDate(invite.invitedAt) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex gap-2">
                <button 
                  class="btn btn-sm btn-success" 
                  @click="acceptInvite(invite.id)"
                  :disabled="isProcessingInvite === invite.id"
                >
                  <span v-if="isProcessingInvite === invite.id" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Accept
                </button>
                <button 
                  class="btn btn-sm btn-danger" 
                  @click="rejectInvite(invite.id)"
                  :disabled="isProcessingInvite === invite.id"
                >
                  Decline
                </button>
                <router-link 
                  :to="{ name: 'BoardGameDetail', params: { id: invite.boardGame.id } }" 
                  class="btn btn-sm btn-outline-primary"
                >
                  View
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User's Board Games Section -->
    <div v-if="user" class="mt-5">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2 class="mb-0">Your Games</h2>
        <router-link v-if="boardGames.length" to="/my-games" class="btn btn-link p-0">
          See all your games &rarr;
        </router-link>
      </div>
      <div v-if="loading" class="text-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      <div v-else-if="boardGames.length === 0" class="alert alert-info">
        You haven't created any board games yet. 
        <router-link to="/create-board-game">Create your first board game!</router-link>
      </div>
      <div v-else class="row row-cols-1 row-cols-md-3 g-4">
        <GameTile v-for="game in boardGames" :key="game.id" :game="game" />
      </div>
    </div>

    <!-- Followed Board Games Section -->
    <div v-if="user" class="mt-5">
      <h2 class="mb-4">Games You Follow</h2>
      <div v-if="loadingFollowed" class="text-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else-if="followedError" class="alert alert-danger">
        {{ followedError }}
      </div>
      <div v-else-if="followedGames.length === 0" class="alert alert-info">
        You haven't followed any board games yet.
        Browse the new games below and follow the ones you like!
      </div>
      <div v-else class="row row-cols-1 row-cols-md-3 g-4">
        <div v-for="game in followedGames" :key="game.id" class="col">
          <div class="card h-100">
            <img 
              :src="game.headerImageUrl || getGameImage(game.type)" 
              class="card-img-top" 
              :alt="`${game.name} board game`"
            >
            <div class="card-body">
              <h5 class="card-title">{{ game.name }}</h5>
              <p class="card-text">
                {{ truncateDescription(game.description) }}
              </p>
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <span class="badge bg-secondary me-1">
                    {{ game.type.charAt(0).toUpperCase() + game.type.slice(1) }}
                  </span>
                  <span class="badge bg-primary">
                    Following
                  </span>
                </div>
                <div class="d-flex gap-1">
                  <router-link
                    :to="{ name: 'BuildBoards', params: { boardGameId: game.id } }"
                    class="btn btn-outline-primary btn-sm"
                  >
                    Boards
                  </router-link>
                  <router-link
                    :to="{ name: 'BoardGameDetail', params: { id: game.id } }"
                    class="btn btn-primary btn-sm"
                  >
                    View
                  </router-link>
                </div>
              </div>
              <div class="mt-2 small text-muted">
                <div class="d-flex align-items-center">
                  <div class="user-icon bg-secondary text-white me-2 d-flex align-items-center justify-content-center">
                    <i class="fas fa-user"></i>
                  </div>
                  <span>Created by {{ game.creatorName || 'Anonymous' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Recently Created Board Games Section -->
    <div class="mt-5">
      <h2 class="mb-4">New Board Games</h2>
      <div v-if="loadingNewGames" class="text-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <!-- Nothing live to show (logged out, offline, or no approved games yet):
           fall back to static examples rather than an empty state. These are
           hardcoded, not loaded from Firebase, so they always render. -->
      <div v-else-if="newBoardGames.length === 0">
        <p class="text-muted mb-4">
          A few examples of what people build here.
          <router-link v-if="!user" to="/register">Create an account</router-link>
          <span v-if="!user"> to start your own.</span>
        </p>
        <div class="row row-cols-1 row-cols-md-3 g-4">
          <div v-for="game in exampleGames" :key="game.id" class="col">
            <div class="card h-100 example-card">
              <div class="card-img-container" style="height: 200px; overflow: hidden;">
                <img
                  :src="exampleCover(game)"
                  class="card-img-top w-100"
                  :alt="`${game.name} — example board game`"
                  style="object-fit: cover;"
                >
              </div>
              <div class="card-body d-flex flex-column">
                <h5 class="card-title mb-1">{{ game.name }}</h5>
                <div class="small text-muted mb-2">{{ game.tagline }}</div>
                <p class="card-text flex-grow-1">{{ game.description }}</p>
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <span class="badge bg-secondary me-1">
                      {{ game.type.charAt(0).toUpperCase() + game.type.slice(1) }}
                    </span>
                    <span class="badge bg-light text-dark border">Example</span>
                  </div>
                  <router-link
                    :to="user ? '/create-board-game' : '/register'"
                    class="btn btn-outline-primary btn-sm"
                  >
                    Build one
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="row row-cols-1 row-cols-md-3 g-4">
        <div v-for="game in newBoardGames" :key="game.id" class="col">
          <div class="card h-100">
            <div class="card-img-container" style="height: 200px; overflow: hidden;">
              <img 
                :src="game.headerImageUrl || getGameImage(game.type)" 
                class="card-img-top w-100" 
                :alt="`${game.name} board game`"
                style="object-fit: cover;"
              >
            </div>
            <div class="card-body">
              <h5 class="card-title">{{ game.name }}</h5>
              <p class="card-text">
                {{ truncateDescription(game.description) }}
              </p>
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <span class="badge bg-secondary me-1">
                    {{ game.type.charAt(0).toUpperCase() + game.type.slice(1) }}
                  </span>
                  <span class="badge bg-info">
                    New
                  </span>
                </div>
                <div class="d-flex gap-1">
                  <router-link
                    :to="{ name: 'BuildBoards', params: { boardGameId: game.id } }"
                    class="btn btn-outline-primary btn-sm"
                  >
                    Boards
                  </router-link>
                  <router-link
                    :to="{ name: 'BoardGameDetail', params: { id: game.id } }"
                    class="btn btn-primary btn-sm"
                  >
                    View
                  </router-link>
                </div>
              </div>
             <!--  <div class="mt-2 small text-muted">
                <div class="d-flex align-items-center">
                  <div class="user-icon bg-secondary text-white me-2 d-flex align-items-center justify-content-center">
                    <i class="fas fa-user"></i>
                  </div>
                  <span>Created by {{ game.creatorName || 'Anonymous' }} on {{ formatDate(game.createdAt) }}</span>
                </div>
              </div> -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA sits after the examples: a visitor who has scrolled the gallery and
         the games has seen the reason to sign up, so this is where asking makes
         sense rather than mid-page. -->
    <div v-if="!user" class="cta-banner text-center mt-5 mb-4">
      <h4 style="font-family: var(--font-heading);">Ready to forge your game?</h4>
      <p class="text-muted mb-3">Join creators already prototyping their next hit.</p>
      <router-link to="/register" class="btn btn-primary btn-lg me-2">
        Create Free Account
      </router-link>
      <router-link to="/login" class="btn btn-outline-secondary btn-lg">
        Sign In
      </router-link>

      <div class="newsletter mt-4">
        <p class="newsletter-label mb-2">Not ready yet? Get occasional updates.</p>
        <form class="newsletter-form" @submit.prevent="subscribeNewsletter">
          <input
            v-model="newsletterEmail"
            type="email"
            class="form-control"
            placeholder="you@example.com"
            aria-label="Email address for updates"
            required
          />
          <button type="submit" class="btn btn-outline-primary" :disabled="newsletterBusy">
            {{ newsletterBusy ? 'Signing up…' : 'Notify me' }}
          </button>
        </form>
        <p v-if="newsletterMessage" class="newsletter-note mt-2" :class="newsletterError ? 'text-danger' : 'text-success'">
          {{ newsletterMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, query, where, getDocs, orderBy, limit, addDoc } from 'firebase/firestore'
import { firestore, getUserInvites, updateEditorStatus, getFollowedBoardGames } from '@/firebase'
import { EXAMPLE_GAMES, exampleCover } from '@/data/exampleGames'
import GameTile from '@/components/GameTile.vue'

export default {
  name: 'MainPage',
  components: { GameTile },
  data() {
    return {
      successMessage: null,
      user: null,
      // Static, not from Firebase — shown when there is nothing live to list.
      exampleGames: EXAMPLE_GAMES,
      boardGames: [],
      loading: false,
      error: null,
      pendingInvites: [],
      isProcessingInvite: null,
      newBoardGames: [],
      loadingNewGames: false,
      newGamesError: null,
      followedGames: [],
      loadingFollowed: false,
      followedError: null,
      newsletterEmail: '',
      newsletterBusy: false,
      newsletterMessage: '',
      newsletterError: false,

      features: [
        {
          title: 'Design Your Game',
          body: 'Rapidly build and test game design ideas.',
          image: '/samples/classic.png',
          alt: 'A character card generated by Sparkforge'
        },
        {
          title: 'Print & Play',
          body: 'Export print-ready PDFs and play tonight.',
          image: '/samples/reference.png',
          alt: 'A landscape reference card generated by Sparkforge'
        },
        {
          title: 'Share & Get Feedback',
          body: 'Invite editors and iterate together.',
          image: '/samples/action.png',
          alt: 'An action card generated by Sparkforge'
        }
      ],

      // Rendered by the real card templates — see scripts/gensamples.py.
      // Split by orientation so the rows wrap cleanly instead of orphaning a
      // landscape card on a line of its own.
      samplePortrait: [
        { src: '/samples/action.png', label: 'Action card' },
        { src: '/samples/classic.png', label: 'Character card' },
        { src: '/samples/trait.png', label: 'Upgrade card' },
        { src: '/samples/minimal.png', label: 'Resource card' }
      ],
      sampleLandscape: [
        { src: '/samples/reference.png', label: 'Reference card' },
        { src: '/samples/wide.png', label: 'Location card' }
      ]
    }
  },
  methods: {
    /**
     * Store a signup. Writes to the `newsletterSignups` collection, which is
     * create-only for everyone and readable by nobody from the client — see
     * firestore.rules. No third-party list provider is wired up, so these sit
     * in Firestore until one is.
     */
    async subscribeNewsletter() {
      const email = (this.newsletterEmail || '').trim().toLowerCase();
      if (!email) return;
      this.newsletterBusy = true;
      this.newsletterMessage = '';
      this.newsletterError = false;
      try {
        await addDoc(collection(firestore, 'newsletterSignups'), {
          email,
          createdAt: new Date().toISOString(),
          source: 'home'
        });
        this.newsletterEmail = '';
        this.newsletterMessage = 'Thanks — we will be in touch.';
      } catch (e) {
        this.newsletterError = true;
        this.newsletterMessage = 'Could not sign you up just now. Please try again.';
      } finally {
        this.newsletterBusy = false;
      }
    },

    exampleCover,
    clearSuccessMessage() {
      this.successMessage = null
    },
    getGameImage(type) {
      // Default game image
      const defaultImage = 'https://storage.googleapis.com/rpg-framework-a73be.appspot.com/CastleIntro.jpg'
      
      // In the future, you can add specific images for different game types
      const gameTypeImages = {
        strategy: defaultImage,
        party: defaultImage,
        cooperative: defaultImage,
        card: defaultImage,
        board: defaultImage,
        other: defaultImage
      }
      
      return gameTypeImages[type] || defaultImage
    },
    truncateDescription(description, maxLength = 100) {
      return description.length > maxLength 
        ? description.substring(0, maxLength) + '...' 
        : description
    },
    async fetchUserBoardGames() {
      if (!this.user) return

      this.loading = true
      this.error = null

      try {
        const boardGamesRef = collection(firestore, 'boardGames')
        const q = query(boardGamesRef, where('userId', '==', this.user.uid))
        
        const querySnapshot = await getDocs(q)
        
        this.boardGames = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .slice(0, 5)  // Limit to 5 board games
          
        // Fetch pending invites
        await this.fetchPendingInvites()
      } catch (err) {
        console.error('Error fetching board games:', err)
        this.error = 'Failed to load board games'
      } finally {
        this.loading = false
      }
    },
    
    async fetchFollowedGames() {
      if (!this.user) return;

      this.loadingFollowed = true;
      this.followedError = null;

      try {
        this.followedGames = await getFollowedBoardGames(this.user.uid);
      } catch (err) {
        console.error('Error fetching followed games:', err);
        this.followedError = 'Failed to load followed games';
      } finally {
        this.loadingFollowed = false;
      }
    },

    async fetchNewBoardGames() {
      this.loadingNewGames = true
      this.newGamesError = null

      try {
        const boardGamesRef = collection(firestore, 'boardGames')
        // Order by createdAt in descending order (newest first)
        const q = query(boardGamesRef, where('approved', '==', true), orderBy('createdAt', 'desc'), limit(5))
        
        const querySnapshot = await getDocs(q)
        
        this.newBoardGames = querySnapshot.docs.map(doc => {
          const data = doc.data()
          
          // Add creator name if not present
          if (!data.creatorName && data.userId) {
            // In a real app, you would fetch the user's name from a users collection
            // For simplicity, we'll just use a placeholder
            data.creatorName = 'Game Creator'
          }
          
          return {
            id: doc.id,
            ...data
          }
        })
      } catch (err) {
        console.error('Error fetching new board games:', err)
        this.newGamesError = 'Failed to load new board games'
      } finally {
        this.loadingNewGames = false
      }
    },
    
    async fetchPendingInvites() {
      if (!this.user || !this.user.email) return
      
      try {
        this.pendingInvites = await getUserInvites(this.user.email)
      } catch (err) {
        console.error('Error fetching pending invites:', err)
      }
    },
    
    async acceptInvite(inviteId) {
      if (!inviteId) return
      
      this.isProcessingInvite = inviteId
      
      try {
        await updateEditorStatus(inviteId, 'accepted')
        
        // Remove the invite from the list
        this.pendingInvites = this.pendingInvites.filter(invite => invite.id !== inviteId)
        
        // Show success message
        this.successMessage = 'You are now an editor for this board game'
      } catch (err) {
        console.error('Error accepting invite:', err)
      } finally {
        this.isProcessingInvite = null
      }
    },
    
    async rejectInvite(inviteId) {
      if (!inviteId) return
      
      this.isProcessingInvite = inviteId
      
      try {
        await updateEditorStatus(inviteId, 'rejected')
        
        // Remove the invite from the list
        this.pendingInvites = this.pendingInvites.filter(invite => invite.id !== inviteId)
      } catch (err) {
        console.error('Error rejecting invite:', err)
      } finally {
        this.isProcessingInvite = null
      }
    },
    
    formatDate(timestamp) {
      if (!timestamp) return 'Unknown date'
      
      // Check if it's a Firebase Timestamp or a JavaScript Date
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  created() {
    // Check for success message from route query
    const { message } = this.$route.query
    if (message) {
      this.successMessage = message
      // Remove the query parameter to prevent showing the message again on refresh
      this.$router.replace({ query: {} })
    }

    // Set up auth state listener
    const auth = getAuth()
    onAuthStateChanged(auth, (user) => {
      this.user = user
      if (user) {
        this.fetchUserBoardGames();
        this.fetchFollowedGames();
      }
    })
    
    // Fetch new board games regardless of authentication status
    this.fetchNewBoardGames()
  }
}
</script>

<style scoped>
.hero-section {
  border-radius: 20px;
  overflow: hidden;
  background-image: url('https://firebasestorage.googleapis.com/v0/b/boardbuilder-f6627.firebasestorage.app/o/board-game-images%2FpjDDQPvGyWmT0NAZHuzC%2F1744150183603_ai_generated_image.png?alt=media&token=f66849d8-2563-46ae-bb7d-a9509b608a82');
  background-size: cover;
  background-position: center;
  min-height: 280px;
  display: flex;
  align-items: center;
}

.hero-overlay {
  width: 100%;
  padding: 3rem 2.5rem;
  background: linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.7) 60%, rgba(255,255,255,0.3) 100%);
  text-align: left;
}

.hero-title {
  font-family: var(--font-heading);
  color: var(--ink);
  font-size: 2.6rem;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 0.25rem;
}

.hero-subtitle {
  font-family: var(--font-handwritten);
  color: var(--pop-orange);
  font-size: 1.6rem;
  margin-bottom: 1.5rem;
}

.hero-cta {
  font-size: 1.1rem;
  padding: 0.7rem 2rem;
}

.btn-block {
  width: 100%;
}

.user-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  background-color: var(--pop-purple);
  color: white;
}

.game-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  font-size: 18px;
  background-color: var(--pop-blue);
  color: white;
}

/* Guest landing styles */
.feature-card {
  border: 2px solid var(--border-sketch, rgba(0,0,0,0.08));
  border-radius: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px var(--shadow-med, rgba(45,45,45,0.12));
}

.feature-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--bg-elevated, #fff8f0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: var(--pop-orange, #ff6b35);
}

.sample-card {
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--border-sketch, rgba(0,0,0,0.08));
  transition: transform 0.2s ease;
}

.sample-card:hover {
  transform: translateY(-3px);
}

.sample-card-header {
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sample-card-icon {
  font-size: 2.4rem;
  color: rgba(255, 255, 255, 0.9);
}

.cta-banner {
  background: var(--bg-elevated, #fff8f0);
  border-radius: 16px;
  padding: 2.5rem 2rem;
  border: 2px dashed var(--pencil-light, #d4c9b5);
}


/* Feature cards now lead with real generated output rather than an icon
   glyph, so the card art needs a consistent stage. */
.feature-art {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1rem 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.035), transparent);
}
.feature-art img {
  max-height: 190px;
  width: auto;
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
}

/* Sample gallery. Portrait and landscape cards share the row, so the items
   are sized by height and left to keep their own aspect ratio. */
.sample-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  justify-content: center;
}
.sample-item {
  margin: 0;
  text-align: center;
  max-width: 100%;
}
.sample-item img {
  height: 220px;
  width: auto;
  max-width: 100%;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  transition: transform 0.15s ease;
}
.sample-item img:hover { transform: translateY(-4px); }
.sample-item figcaption {
  margin-top: 0.5rem;
  font-size: 0.82rem;
  color: var(--ink-light, #7a7a7a);
}

.newsletter {
  max-width: 460px;
  margin: 0 auto;
}
.newsletter-label { font-size: 0.9rem; color: var(--ink-light, #7a7a7a); }
.newsletter-form { display: flex; gap: 0.5rem; }
.newsletter-form .form-control { flex: 1; min-width: 0; }
.newsletter-note { font-size: 0.85rem; }

@media (max-width: 575.98px) {
  .sample-item img { height: 150px; }
  .feature-art img { max-height: 150px; }
  .newsletter-form { flex-wrap: wrap; }
  .newsletter-form .btn { width: 100%; }
}
</style>